import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';

import {
    FormControl, Select, MenuItem, Input, ListItemText, Checkbox, FormLabel, FormHelperText, Chip,
} from '@material-ui/core';
import { toLookup, toBoolLookup } from '../../youwell-common/arrayUtils';


const styles = theme => ({
    root: {
        background: theme.palette.common.white,
        padding: theme.spacing(2),
    },
    error: {
        border: theme.borders.normal,
        borderColor: theme.palette.error.main,
    },
    inner: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    label: {
        '&$focused': {
            color: theme.palette.grey[600],
        },
    },
    inputSelect: {
        marginLeft: theme.spacing(2),
        flex: '1 1 auto',
        '& $disabled': {
            color: theme.palette.common.black,
        },
        'label + &': { marginTop: 0 },
    },
    inputInput: {
        padding: `0 ${theme.spacing(3)}px 0 0`,
        '&:focus': {
            backgroundColor: theme.palette.common.white,
        },
    },
    inputIconDisabled: {
        display: 'none',
    },
    focused: {
    },
    disabled: {
    },
    chip: {
        marginRight: theme.spacing(0.5),
    },
});

class Selector extends React.Component {
    handleChangeMultiple = (event) => {
        const selection = event.target.value.map(id => ({ id }));
        this.props.onChange({ target: { value: selection } });
    }

    renderValues = (selected) => {
        const {
            items, renderAs, classes, translate,
        } = this.props;
        const itemLookup = toLookup(items);

        if (!selected || selected.length === 0) {
            return itemLookup[''] ? itemLookup[''].name : null;
        }

        switch (renderAs) {
        case 'count': {
            return translate('selector.countSelected', { count: selected.length });
        }
        case 'ids': {
            return selected.join(', ');
        }
        case 'chip': {
            return (
                <div className={classes.chips}>
                    {selected.map(val => (
                        <Chip key={val} label={itemLookup[val].name} className={classes.chip} />
                    ))}
                </div>
            );
        }
        case 'list':
        default: {
            return selected.map(d => (itemLookup[d] ? itemLookup[d].name || itemLookup[d].id : d)).join(', ');
        }
        }
    }

    render() {
        const {
            classes, translate, className, value, values, label, disabled, items, id, onChange, multiple, addEmptyItemInList, validationError, fullWidth,
        } = this.props;

        if (!items || items.length < 1) {
            return null;
        }

        const selectedValues = multiple ? toBoolLookup(values, 'id') : null;
        const selectedValuesArray = multiple && values ? values.map(d => d.id) : [];
        const itemsWithEmpty = addEmptyItemInList ? [{ id: '', name: '...' }, ...items] : items;

        return (
            <FormControl
                className={classnames(classes.root, className, { [classes.error]: !!validationError })}
                error={!!validationError}
                fullWidth={fullWidth}
            >
                <div className={classes.inner}>
                    {label && (
                        <FormLabel
                            className={classes.label}
                            classes={{ focused: classes.focused }}
                            htmlFor={id}
                        >
                            {`${label}:`}
                        </FormLabel>
                    )}
                    <Select
                        className={classnames(classes.inputSelect, { })}
                        classes={{
                            selectMenu: classes.inputInput,
                            disabled: classes.disabled,
                            icon: disabled ? classes.inputIconDisabled : null,
                        }}
                        multiple={multiple}
                        disabled={disabled}
                        autoWidth
                        disableUnderline
                        displayEmpty
                        value={multiple ? selectedValuesArray : value}
                        onChange={multiple ? this.handleChangeMultiple : onChange}
                        input={<Input id={id} />}
                        renderValue={multiple ? this.renderValues : null}
                    >
                        {itemsWithEmpty.map(item => (
                            multiple ? (
                                <MenuItem key={item.id} value={item.id}>
                                    <Checkbox checked={selectedValues[item.id] || false} />
                                    <ListItemText primary={item.name || item.id} />
                                </MenuItem>
                            ) : (
                                <MenuItem key={item.id} value={item.id}>
                                    { item.name || item.id}
                                </MenuItem>
                            )
                        ))}
                    </Select>
                </div>
                {validationError && (
                    <FormHelperText>{translate(validationError)}</FormHelperText>
                )}
            </FormControl>
        );
    }
}


Selector.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        name: PropTypes.string,
    })),
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    values: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })),
    renderAs: PropTypes.oneOf(['list', 'chip', 'count', 'ids']),
    label: PropTypes.string,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    addEmptyItemInList: PropTypes.bool,
    validationError: PropTypes.string,
    fullWidth: PropTypes.bool,
};

Selector.defaultProps = {
    className: null,
    items: null,
    label: null,
    value: '',
    values: [],
    disabled: false,
    multiple: false,
    addEmptyItemInList: false,
    validationError: null,
    fullWidth: false,
    renderAs: 'list',
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
});

export default connect(mapStateToProps)(withStyles(styles)(Selector));
